import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileText, Award, Eye, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const OurStandards = () => {
  return (
    <div className="page-standards-new">
      {/* Hero */}
      <section className="standards-hero-new">
        <div className="standards-hero-bg">
          <div className="standards-hero-overlay"></div>
        </div>
        <div className="standards-hero-content">
          <span className="hero-label">Quality Assurance</span>
          <h1 className="standards-headline">OUR STANDARDS</h1>
          <p className="standards-subline">
            Not just anyone gets listed. Here's what it takes.
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="stats-bar">
        <div className="container-premium">
          <div className="stats-row">
            <div className="stat-block">
              <span className="stat-num">100%</span>
              <span className="stat-text">Gas Safe Verified</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-block">
              <span className="stat-num">100%</span>
              <span className="stat-text">Insurance Checked</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-block">
              <span className="stat-num">Ongoing</span>
              <span className="stat-text">Monitoring</span>
            </div>
          </div>
        </div>
      </section>

      {/* Vetting Process - Visual Steps */}
      <section className="vetting-section-new">
        <div className="container-premium">
          <div className="section-header-premium">
            <span className="section-label">The Process</span>
            <h2 className="section-title-premium">HOW WE VET ENGINEERS</h2>
          </div>

          <div className="vetting-timeline">
            <div className="vetting-step">
              <div className="step-marker">
                <Shield size={24} />
              </div>
              <div className="step-content">
                <h3>Gas Safe Verification</h3>
                <p>Direct verification with Gas Safe Register. No expired or fake certificates.</p>
              </div>
            </div>

            <div className="vetting-step">
              <div className="step-marker">
                <FileText size={24} />
              </div>
              <div className="step-content">
                <h3>Insurance & Credentials</h3>
                <p>£5M+ public liability insurance. Business registration confirmed.</p>
              </div>
            </div>

            <div className="vetting-step">
              <div className="step-marker">
                <Award size={24} />
              </div>
              <div className="step-content">
                <h3>Experience Review</h3>
                <p>Portfolio assessment. Customer references. Quality standards check.</p>
              </div>
            </div>

            <div className="vetting-step">
              <div className="step-marker">
                <Eye size={24} />
              </div>
              <div className="step-content">
                <h3>Ongoing Monitoring</h3>
                <p>Annual re-verification. Review monitoring. Standards maintained.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Check - Bento Grid */}
      <section className="checks-section-new">
        <div className="container-premium">
          <div className="section-header-premium">
            <span className="section-label">The Details</span>
            <h2 className="section-title-premium">WHAT WE CHECK</h2>
          </div>

          <div className="checks-bento">
            <div className="check-card large">
              <Shield className="check-icon" size={32} />
              <h3>Gas Safe Registration</h3>
              <ul>
                <li><CheckCircle size={16} /> Valid certificate verification</li>
                <li><CheckCircle size={16} /> Qualification confirmation</li>
                <li><CheckCircle size={16} /> Complaint history check</li>
                <li><CheckCircle size={16} /> Regular re-verification</li>
              </ul>
            </div>

            <div className="check-card">
              <FileText className="check-icon" size={32} />
              <h3>Insurance</h3>
              <ul>
                <li><CheckCircle size={16} /> £5M+ public liability</li>
                <li><CheckCircle size={16} /> Employer's liability</li>
                <li><CheckCircle size={16} /> Professional indemnity</li>
              </ul>
            </div>

            <div className="check-card">
              <Award className="check-icon" size={32} />
              <h3>Experience</h3>
              <ul>
                <li><CheckCircle size={16} /> Years trading verified</li>
                <li><CheckCircle size={16} /> Portfolio reviewed</li>
                <li><CheckCircle size={16} /> References checked</li>
              </ul>
            </div>

            <div className="check-card accent">
              <Eye className="check-icon" size={32} />
              <h3>Ongoing</h3>
              <ul>
                <li><CheckCircle size={16} /> Annual credential check</li>
                <li><CheckCircle size={16} /> Review monitoring</li>
                <li><CheckCircle size={16} /> Standards enforcement</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="why-section-new">
        <div className="container-premium">
          <div className="why-card">
            <div className="why-content">
              <h2>WHY THIS MATTERS</h2>
              <p className="why-highlight">
                Gas work isn't like other trades. A mistake can mean 
                carbon monoxide poisoning, fires, or explosions.
              </p>
              <p>
                That&apos;s why we don&apos;t just list anyone. We thoroughly check 
                every engineer&apos;s Gas Safe registration before they go live.
              </p>
            </div>
            <div className="why-visual">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/c44e0078-dad0-4daf-ae5e-afa063e2af0b/images/16afc35c9c0c1b4e31f969fdb52de0adf2b441e2ebd416cf6fc8d0025d693950.png" 
                alt="Carbon monoxide detector - gas safety" 
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="standards-cta-new">
        <div className="container-premium">
          <div className="cta-split">
            <div className="cta-card homeowner">
              <h3>Need a Gas Engineer?</h3>
              <p>Find vetted professionals in your area</p>
              <Link to="/find-engineer">
                <Button className="btn-cta-light">
                  Search Now <ArrowRight size={18} />
                </Button>
              </Link>
            </div>
            <div className="cta-card engineer">
              <h3>Are You an Engineer?</h3>
              <p>Meet our standards? Join the network</p>
              <Link to="/for-engineers">
                <Button className="btn-cta-green">
                  Apply Now <ArrowRight size={18} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurStandards;
